import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Search, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { download } from "@/components/phones/PhoneRegister";
import { api, errMsg } from "@/lib/api";

export const TyreImportDialog = ({ kind, sid, isAdmin, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const isStock = kind === "stock";
  const base = isStock ? "/tyres/stock" : "/tyres/changes";
  const send = async (confirm) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("station_id", isAdmin ? sid : "");
    fd.append("confirm", confirm);
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/import`, fd);
      if (confirm) { toast.success(isStock ? `Magazzino aggiornato (${data.count} gomme)` : `Importati ${data.count} cambi gomme`); onDone(); } else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid={`tyre-import-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isStock ? "Importa magazzino di base" : "Importa storico cambi gomme"}</DialogTitle>
          <DialogDescription>
            {isStock
              ? "Colonne: MARCA, MODELLO GOMMA, MISURA, STAGIONE, NUOVE, USATE, SCORTA MINIMA. Le quantità del file sostituiscono quelle attuali; le celle vuote lasciano il valore com'è. Il file scaricato contiene già tutte le gomme con le quantità di oggi."
              : "Colonne: TARGA, DATA, KM, ASSE (anteriori, posteriori, tutte), MARCA, MODELLO GOMMA, MISURA, STAGIONE; facoltative STATO (nuove/usate), GOMMISTA, NOTE. Le gomme smontate si ricavano dal cambio precedente. Lo storico importato non tocca il magazzino, ma puoi selezionarlo negli ordini."}
            {isAdmin && " Le gomme non presenti in Tipologie vengono create in automatico."}
          </DialogDescription>
        </DialogHeader>
        <Button variant="outline" onClick={() => download(`${base}/template`, isStock && isAdmin ? { station_id: sid } : {}, isStock ? "magazzino_gomme_base.xlsx" : "modello_storico_gomme.xlsx")} data-testid={`tyre-import-${kind}-template`}>
          <Download className="mr-2 h-4 w-4" /> Scarica il file da compilare
        </Button>
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="truncate">{file ? file.name : "Scegli file Excel (.xlsx)..."}</span>
          <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid={`tyre-import-${kind}-file`} />
        </label>
        <Button onClick={() => send(false)} disabled={!file || busy} data-testid={`tyre-import-${kind}-preview`}><Search className="mr-2 h-4 w-4" /> Analizza file</Button>
        {preview && (
          <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid={`tyre-import-${kind}-result`}>
            <p><b>{preview.count}</b> righe valide{preview.vehicles != null && ` su ${preview.vehicles} furgoni`}</p>
            {preview.new_types.length > 0 && <div className={`rounded-md p-2 text-xs ${preview.blocked ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-800"}`}>
              <p className="mb-1 font-semibold">{preview.blocked || `${preview.new_types.length} nuove tipologie verranno create:`}</p>
              {preview.new_types.map((t) => <p key={t}>{t}</p>)}</div>}
            {preview.errors.length > 0 && <div className="max-h-40 overflow-auto rounded-md bg-rose-50 p-2 text-xs text-rose-700">
              <p className="mb-1 font-semibold">{preview.errors.length} righe scartate:</p>
              {preview.errors.map((w, i) => <p key={i}>Riga {w.row} ({w.text}): {w.error}</p>)}</div>}
            <Button onClick={() => send(true)} disabled={busy || !preview.count || !!preview.blocked} data-testid={`tyre-import-${kind}-confirm`}><Upload className="mr-2 h-4 w-4" /> Carica</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
